import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogAPI } from '../utils/api';
import SEO from '../components/SEO';
import SocialShare from '../components/SocialShare';
import config from '../config/config';
import { formatBlogContent } from '../utils/formatBlogContent';
import './BlogPost.css';
import artistImage from '../assets/images/banner.png';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await blogAPI.getBySlug(slug);
      const postData = response.data;
      setPost(postData);

      // Fetch related posts
      try {
        const relatedResponse = await blogAPI.getRelated(slug);
        setRelatedPosts(relatedResponse.data);
      } catch (err) {
        console.error('Error fetching related posts:', err);
      }

      // Scroll to top
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content?.split(/\s+/).length || 0;
    return Math.ceil(words / wordsPerMinute);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <>
        <SEO title="Loading Article..." description="Loading blog article" url={`/blog/${slug}`} />
        <div className="spinner" role="status" aria-label="Loading article"></div>
      </>
    );
  }

  if (!post) {
    return (
      <>
        <SEO title="Article Not Found" description="The requested article could not be found." url={`/blog/${slug}`} />
        <div className="error-message container">
          <h1>Article Not Found</h1>
          <p>Sorry, the article you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog" className="btn-primary">Browse All Articles</Link>
        </div>
      </>
    );
  }

  const readingTime = calculateReadingTime(post.content);
  const imageUrl = post.image_url ? `${config.BASE_API_URL}${post.image_url}` : {artistImage};

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt || post.meta_description,
    "image": imageUrl,
    "datePublished": post.created_at,
    "dateModified": post.updated_at,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Canvaart",
      "logo": {
        "@type": "ImageObject",
        "url": "https://canvaart.in/logo192.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://canvaart.in/blog/${post.slug}`
    },
    "articleSection": post.category,
    "keywords": post.meta_keywords || post.tags?.join(', ')
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://canvaart.in/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://canvaart.in/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://canvaart.in/blog/${post.slug}`
      }
    ]
  };

  return (
    <article className="blog-post-page" itemScope itemType="https://schema.org/BlogPosting">
      <SEO
        title={post.title}
        description={post.meta_description || post.excerpt || `Read ${post.title} on the Canvaart blog`}
        keywords={post.meta_keywords || `${post.category} art, canvas painting, ${post.tags?.join(', ') || ''}`}
        image={imageUrl}
        url={`/blog/${post.slug}`}
        type="article"
        schema={[articleSchema, breadcrumbSchema]}
      />

      {/* Article Header */}
      <header className="blog-post-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {post.category && (
              <Link to={`/blog?category=${encodeURIComponent(post.category)}`} className="blog-post-category">
                {post.category}
              </Link>
            )}
            <h1 itemProp="headline">{post.title}</h1>
            <div className="blog-post-meta">
              <div className="blog-post-author">
                <span className="author-avatar">{post.author?.charAt(0)}</span>
                <span itemProp="author">{post.author}</span>
              </div>
              <span className="blog-meta-dot">•</span>
              <time itemProp="datePublished" dateTime={post.created_at}>
                {formatDate(post.created_at)}
              </time>
              <span className="blog-meta-dot">•</span>
              <span>{readingTime} min read</span>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Featured Image */}
      <div className="blog-post-hero-image">
        <img src={imageUrl} alt={post.title} itemProp="image" />
      </div>

      {/* Article Content */}
      <div className="container blog-post-layout">
        <div className="blog-post-content-wrapper">
          <motion.div
            className="blog-post-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {post.excerpt && (
              <p className="blog-post-excerpt" itemProp="description">{post.excerpt}</p>
            )}
            <div
              className="blog-post-body"
              itemProp="articleBody"
              dangerouslySetInnerHTML={{ __html: formatBlogContent(post.content) }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="blog-post-tags">
                <span className="tags-label">Tags:</span>
                {post.tags.map((tag, index) => (
                  <span key={index} className="blog-tag">{tag}</span>
                ))}
              </div>
            )}

            {/* Share */}
            <div className="blog-post-share">
              <SocialShare
                title={post.title}
                url={`/blog/${post.slug}`}
                image={imageUrl}
                description={post.excerpt || `Read ${post.title} on Canvaart`}
              />
            </div>

            {/* Author Box */}
            <div className="blog-post-author-box">
              <div className="author-box-avatar">
                {post.author?.charAt(0)}
              </div>
              <div className="author-box-info">
                <h3>Written by {post.author}</h3>
                <p>
                  Professional canvas artist specializing in abstract art, portraits,
                  landscapes, and custom commissions. Passionate about helping people
                  discover the perfect art for their spaces.
                </p>
                <Link to="/about" className="author-box-link">More about the artist →</Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sticky Sidebar */}
        <aside className="blog-post-sidebar" aria-label="Article sidebar">
          <div className="sidebar-sticky">
            <div className="sidebar-section">
              <h3>Share This Article</h3>
              <SocialShare
                title={post.title}
                url={`/blog/${post.slug}`}
                image={imageUrl}
                description={post.excerpt || `Read ${post.title} on Canvaart`}
              />
            </div>

            <div className="sidebar-section">
              <h3>Reading Time</h3>
              <p className="reading-time">{readingTime} minutes</p>
            </div>

            <div className="sidebar-section">
              <h3>Published</h3>
              <p className="publish-date">{formatDate(post.created_at)}</p>
            </div>

            <div className="sidebar-section cta-sidebar">
              <h3>Commission Custom Art</h3>
              <p className="cta-text">Have a vision? Let's create a unique painting just for you.</p>
              <Link to="/custom-request" className="btn-primary sidebar-btn">Request Custom Art</Link>
            </div>
          </div>
        </aside>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="blog-related-section" aria-label="Related articles">
          <div className="container">
            <h2>You May Also Like</h2>
            <div className="blog-related-grid" role="list">
              {relatedPosts.map((rp, index) => (
                <motion.article
                  key={rp.id}
                  className="blog-related-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  role="listitem"
                >
                  <Link to={`/blog/${rp.slug}`} className="blog-related-link">
                    <div className="blog-related-image">
                      <img
                        src={rp.image_url ? `${config.BASE_API_URL}${rp.image_url}` : {artistImage} }
                        alt={rp.title}
                        loading="lazy"
                      />
                    </div>
                    <div className="blog-related-content">
                      <span className="blog-related-category">{rp.category}</span>
                      <h3>{rp.title}</h3>
                      <p className="blog-related-excerpt">{rp.excerpt}</p>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default BlogPost;
